import { useEffect, useCallback, useRef, useMemo } from 'react'
import { collection, query, where, doc, deleteField } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { updateDoc, deleteDoc } from '../lib/firestore'
import { createSetInterest } from '../lib/interestWrites'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { useGuestSetPicks } from './useGuestSetPicks'
import { nextStatus, writeGuestPick } from '../lib/guestSetPicks'
import { runMutation } from '../lib/firestoreMutation'
import { track, type PartialRangeEntryPoint, type SetInterestEntryPoint } from '../lib/analytics'
import type { InterestStatus, PartialRange, SetInterest } from '../types'
import { fbt } from 'fbtee'

interface SetInterestDoc {
  id: string
  status: InterestStatus | undefined
  partialRange: PartialRange | undefined
}

const EMPTY_MAP: Map<string, SetInterestDoc> = new Map()
const EMPTY_RANGES: Map<string, PartialRange> = new Map()

export function useSetInterests(
  userId: string | null,
  eventId: string,
  options?: {
    onMarked?: () => void
    onMarkedInterested?: (setId: string) => void
    onMarkedGoing?: (setId: string) => void
    /** With no `userId`, keep the viewer's hearts on this device instead of doing nothing. */
    guestLocal?: boolean
  },
) {
  const ref = userId
    ? (query(
        collection(db, 'setInterests'),
        where('userId', '==', userId),
        where('eventId', '==', eventId),
      ) as Query<SetInterest>)
    : null
  const { data, loading, error } = useFirestoreCollection<SetInterest>(ref)
  const guestPicks = useGuestSetPicks(eventId)
  const localPicks = options?.guestLocal && !userId ? guestPicks : null

  const onMarkedRef = useRef(options?.onMarked)
  const onMarkedInterestedRef = useRef(options?.onMarkedInterested)
  const onMarkedGoingRef = useRef(options?.onMarkedGoing)
  useEffect(() => {
    onMarkedRef.current = options?.onMarked
    onMarkedInterestedRef.current = options?.onMarkedInterested
    onMarkedGoingRef.current = options?.onMarkedGoing
  })

  // Map from setId → { doc ID, status, range } so update/delete are direct writes
  // with no preceding read.
  const setInterestDocs = useMemo(
    () =>
      userId
        ? new Map(
            data.map(d => [
              d.setId,
              { id: d.id, status: d.status, partialRange: d.partialRange },
            ]),
          )
        : EMPTY_MAP,
    [userId, data],
  )

  const partialRangeBySetId = useMemo(() => {
    if (setInterestDocs.size === 0) return EMPTY_RANGES
    const ranges = new Map<string, PartialRange>()
    for (const [setId, { partialRange }] of setInterestDocs) {
      if (partialRange) ranges.set(setId, partialRange)
    }
    return ranges.size === 0 ? EMPTY_RANGES : ranges
  }, [setInterestDocs])

  const { interestedSetIds, goingSetIds } = useMemo(() => {
    const interested = new Set<string>()
    const going = new Set<string>()
    const statuses = localPicks ?? new Map([...setInterestDocs].map(([setId, d]) => [setId, d.status]))
    for (const [setId, status] of statuses) {
      if (status === 'going') going.add(setId)
      else interested.add(setId)
    }
    return { interestedSetIds: interested, goingSetIds: going }
  }, [setInterestDocs, localPicks])

  const cycleSetInterest = useCallback(
    async (setId: string, entryPoint: SetInterestEntryPoint) => {
      if (localPicks) {
        const current = localPicks.get(setId) ?? 'none'
        const next = nextStatus(current)
        writeGuestPick(eventId, setId, next === 'none' ? null : next)
        // The ensure* callbacks are no-ops here: callers pass signedInUid to useEventInterest.
        if (current === 'none') {
          onMarkedRef.current?.()
          onMarkedInterestedRef.current?.(setId)
        }
        if (next === 'going') onMarkedGoingRef.current?.(setId)
        track('set_interest_toggled', { status: next, event_id: eventId, entry_point: entryPoint })
        return
      }
      if (!userId) return
      const existing = setInterestDocs.get(setId)
      const current: InterestStatus | 'none' = existing?.status === 'going'
        ? 'going'
        : existing
          ? 'interested'
          : 'none'
      const next = nextStatus(current)
      await runMutation(fbt("Couldn't update set interest", 'Error toast when toggling interest in a set fails'), async () => {
        if (!existing) {
          await createSetInterest(userId, eventId, setId, 'interested')
          onMarkedRef.current?.()
          onMarkedInterestedRef.current?.(setId)
        } else if (next === 'none') {
          await deleteDoc(doc(db, 'setInterests', existing.id))
        } else {
          await updateDoc(doc(db, 'setInterests', existing.id), { status: next })
          if (next === 'going') onMarkedGoingRef.current?.(setId)
        }
        track('set_interest_toggled', {
          status: next,
          event_id: eventId,
          entry_point: entryPoint,
        })
      })
    },
    [userId, eventId, setInterestDocs, localPicks],
  )

  // Checking in at a set is the strongest commitment there is, so it promotes
  // an unmarked or interested set straight to going. No-op when already going.
  const markSetGoing = useCallback(
    async (setId: string) => {
      if (!userId) return
      const existing = setInterestDocs.get(setId)
      if (existing?.status === 'going') return
      if (existing) {
        await updateDoc(doc(db, 'setInterests', existing.id), { status: 'going' })
      } else {
        await createSetInterest(userId, eventId, setId, 'going')
        onMarkedRef.current?.()
      }
      onMarkedGoingRef.current?.(setId)
    },
    [userId, eventId, setInterestDocs],
  )

  // A range says "I'll be there for this slice", which is a commitment — so it
  // only lives on a `going` doc and setting one promotes an `interested` doc in
  // the same write. Clearing it leaves the status alone; they're still going,
  // just for the whole set again.
  const updateSetPartialRange = useCallback(
    async (setId: string, partialRange: PartialRange | null, entryPoint: PartialRangeEntryPoint = 'set_dialog') => {
      if (!userId) return
      const existing = setInterestDocs.get(setId)
      await runMutation(fbt("Couldn't update your set time", 'Error toast when setting the part of a set the user will attend fails'), async () => {
        if (!existing) {
          // The picker only renders on a hearted set, so this is defensive.
          await createSetInterest(userId, eventId, setId, 'going', partialRange ?? undefined)
        } else if (partialRange) {
          await updateDoc(doc(db, 'setInterests', existing.id), {
            status: 'going',
            partialRange,
          })
        } else {
          await updateDoc(doc(db, 'setInterests', existing.id), {
            partialRange: deleteField(),
          })
        }
        if (partialRange) {
          track('set_partial_range_set', {
            event_id: eventId,
            start_min: partialRange.startMin,
            end_min: partialRange.endMin,
            entry_point: entryPoint,
          })
        } else {
          track('set_partial_range_cleared', { event_id: eventId, entry_point: entryPoint })
        }
      })
    },
    [userId, eventId, setInterestDocs],
  )

  return {
    interestedSetIds,
    goingSetIds,
    partialRangeBySetId,
    loading,
    error,
    cycleSetInterest,
    markSetGoing,
    updateSetPartialRange,
  }
}
